import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import Header from './Header';
import Footer from './Footer';
import './PaymentSuccess.css';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, fetchCredits } = useUser();
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      navigate('/');
      return;
    }

    // Refresh user credits after successful payment
    if (fetchCredits) {
      fetchCredits();
    }

    // Fetch payment details
    fetchPaymentDetails();

    setTimeout(() => {
      setLoading(false);
    }, 1500);
  }, [searchParams, navigate, fetchCredits]);

  const fetchPaymentDetails = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/billing/history`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.paymentHistory && data.paymentHistory.length > 0) {
          // Get the most recent payment
          setPaymentDetails(data.paymentHistory[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching payment details:', error);
    }
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="neural-glass-homepage">
      <div className="neural-background"></div>
      
      <div className="geometric-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>

      <Header setShowAuthModal={setShowAuthModal} />

      <main className="payment-success-main">
        <div className="payment-success-container glass">
          {loading ? (
            <div className="payment-loading">
              <div className="success-spinner"></div>
              <h2>Processing your payment...</h2>
            </div>
          ) : (
            <div className="payment-success-content">
              <div className="success-icon">
                <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="50" cy="50" r="45" fill="none" stroke="url(#successGradient)" strokeWidth="4"/>
                  <polyline points="30,50 45,65 70,35" fill="none" stroke="url(#successGradient)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
                  <defs>
                    <linearGradient id="successGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{stopColor:'#4caf50'}}/>
                      <stop offset="100%" style={{stopColor:'#8bc34a'}}/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>

              <h1>Payment Successful!</h1>
              <p className="success-message">
                Your subscription has been activated successfully. You now have unlimited access to all features.
              </p>

              {paymentDetails && (
                <div className="payment-details-box">
                  <h3>Payment Details</h3>
                  <div className="detail-row">
                    <span className="detail-label">Amount Paid:</span>
                    <span className="detail-value">${parseFloat(paymentDetails.amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Plan:</span>
                    <span className="detail-value">{paymentDetails.plan_type}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Billing Period:</span>
                    <span className="detail-value">
                      {formatDate(paymentDetails.billing_period_start)} - {formatDate(paymentDetails.billing_period_end)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Payment Date:</span>
                    <span className="detail-value">{formatDate(paymentDetails.created_at)}</span>
                  </div>
                  {paymentDetails.auto_payment_enabled && (
                    <div className="auto-payment-notice">
                      <span className="auto-icon">🔄</span>
                      <span>Auto-renewal is enabled</span>
                    </div>
                  )}
                </div>
              )}

              <div className="success-features">
                <h3>What You Get:</h3>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Unlimited Interview Sessions</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Unlimited Resume Builder</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Unlimited Cover Letters</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Unlimited Interview Questions</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Unlimited AI Quiz</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>24/7 Technical Support</span>
                </div>
              </div>

              <div className="success-actions">
                <button className="dashboard-btn" onClick={handleGoToDashboard}>
                  Go to Dashboard
                </button>
                <button className="home-btn" onClick={() => navigate('/')}>
                  Return to Home
                </button>
              </div>

              <div className="success-note">
                <p>
                  <strong>📧 Confirmation Email:</strong> A receipt has been sent to your email address.
                </p>
                <p>
                  <strong>💳 Manage Billing:</strong> You can manage your subscription and billing information from your dashboard.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentSuccess;